import { Component } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { isEmpty, omit, pathOr, pick } from 'ramda'
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs'
import { LanguageConfig } from 'src/app/app.models'
import { GFFP_FIELDS_CATEGORY } from 'src/app/gffp/gffp.models'
import { countriesInfo, CountryDetails, getCountryDetails } from 'src/app/store/countries'
import { DpGetEnums, DpSendCommit, DpSendCommitSuccess, DpSendValidate, DpSendValidateSuccess } from 'src/app/store/digital-payment/digital-payment.action'
import { selectEnumFields } from 'src/app/store/enumeration'
import { FeeQuoteData, isWalletFeeEnabled, selectFeeQuoteState } from 'src/app/store/fee-quote'
import { getCurrentLabelsJson } from 'src/app/store/labels'
import { selectQueryParams } from 'src/app/store/query-params'
import { serviceOptionsState } from 'src/app/store/service-options'
import { stateInfo } from 'src/app/store/states'
import { getSelectedLocationTrns, PaymentMethod, selectDestinationCountryData, selectPaymentOption, selectSelectedBankAccount, selectsendCommitResponse, selectTransactionState, TransactionState } from 'src/app/store/transaction'
import { PaymentMethodOption, PaymentOption, PaymentOptionType } from '../payment-method/payment-method.models'
import { selectAuthState, selectFirebaseAuthToken } from 'src/app/store/auth/auth.selectors'
import { DpSendValidatePayload } from './digital-payment.models'
import { ErrorService } from 'src/app/services/error.service'
import { BankAccountData, BankAccountMode } from './bank-account/bank-account.models'
import { MapService } from 'src/app/services/map.service'

@Component({
    selector: 'app-digital-payment',
    templateUrl: './digital-payment.component.html',
    styleUrls: ['./digital-payment.component.scss'],
    standalone: false
})
export class DigitalPaymentComponent {
  public steps: { [id: string]: number } = {
    digitalPaymentLanding: 0,
    bankAccount: 1,
    gffp: 2,
    paymentPlatform: 3,
    summary: 4,
    confirmation: 5
  }
  
  private _gffpFieldNames: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(undefined!)
  public readonly gffpFieldNames$: Observable<string[]> = this._gffpFieldNames.asObservable()
  
  private _sendValResponseError$: BehaviorSubject<Error[] | null> = new BehaviorSubject<Error[] | null>(null)
  public sendValResponseError$ = this._sendValResponseError$.asObservable()

  private _summaryData$: BehaviorSubject<any> = new BehaviorSubject<any>(undefined)
  public readonly summaryData$: Observable<any> = this._summaryData$.asObservable()

  public paymentMethod: PaymentOptionType | null = null
  public selectedBankAccount: BankAccountData | null = null
  public bankMode: BankAccountMode = 'select'
  public payoutOptionCode: string
  private payoutOptionRoutingCode: string
  private risk: string | null
  private transactionId: string
  public languageList: LanguageConfig[] = []
  public cmsLabels: any // TODO: Tim: Fix types
  public currentStep: number = 0
  private country: string
  public exchangeRate: number | string
  public launchPaymentPlatformSdk: boolean = false
  private isNewBankAccount: boolean = false
  public google: any
  private isSignInEnabled: boolean = false
  public isFirebaseEnabled: boolean = false

  private sendValidatePayload: DpSendValidatePayload // Todo: Tim: #may6 - create interface for sendValidatePayload type
  public commitResponse: any = null

  private endpointVersion: string
  public locale: string
  public operation: string // 'withdraw' | 'deposit' // Todo: Tim: fix types
  public walletProvider: string
  public subdivisionOptions: any[] // Todo: tim: fix types
  private transactionData: TransactionState
  public firebaseAuthToken: string
  private userDigitalProfileId: string
  private destinationCountryCode: string
  private destinationCountrySubdivisionCode: string
  public sendAmount: number
  public sendCurrency: string
  public payoutCurrency: string

  public cmsLabels$: Observable<any> // TODO: Tim: Fix types
  public paymentOption$: Observable<PaymentOption | null | undefined>
  public enumerationsResponse$: Observable<any> | undefined // Todo: tim: fix types
  public isWalletFeeEnabled$: Observable<any> | undefined // Todo: tim: fix types
  public selectedLocation$: Observable<any> | undefined // Todo: tim: fix types
  public countries$: Observable<CountryDetails[]> | undefined
  private subdivisionOptions$: Observable<any> | undefined // Todo: tim: fix types
  private feeQuoteState$: Observable<FeeQuoteData>

  private destroy$: Subject<void> = new Subject<void>()
  
  constructor(
    private store: Store<any>,
    private actions$: Actions,
    private router: Router,
    private errorService: ErrorService,
    private mapService: MapService, // Todo: only added to make confirmation work for DP flow. #refactor
    
  ) {
    this.mapService.loadMap(() => {
      this.google = google // Todo: only added to make confirmation work for DP flow. #refactor
    })
  }

  ngOnInit() {
    this.store.select(serviceOptionsState)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((serviceOptions) => {
        this.languageList = serviceOptions?.uiConfigs[0]?.languageList || []
        this.transactionId = serviceOptions.transactionId!;
        if (serviceOptions.config) {
          this.isSignInEnabled = serviceOptions.config?.isSignInEnabled ?? false
          this.isFirebaseEnabled = serviceOptions.config?.isFirebaseEnabled ?? false
          if (this.isSignInEnabled === false) {
            this.navigateToTransactionFlow()          
          }
        } else {
          this.navigateToTransactionFlow()
        }
    })

    this.store.select(selectFirebaseAuthToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe(firebaseAuthToken => {
        this.firebaseAuthToken = firebaseAuthToken
    })

    this.store.select(selectQueryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        this.locale = queryParams.locale
        this.operation = queryParams.operation ?? ''
        this.walletProvider = queryParams.walletProvider ?? ''
        this.endpointVersion = queryParams.endpoint ?? 'v2'
      })
    
    this.store.select(getCountryDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        this.country = country?.code ?? ''
      })

    
    this.store.select(selectPaymentOption)
      .pipe(takeUntil(this.destroy$))
      .subscribe(paymentOptions => {
        this.paymentMethod = paymentOptions?.optionType ?? null
      this.payoutOptionCode = paymentOptions?.serviceOptionData?.payoutOptionCode ?? ''
      this.payoutOptionRoutingCode = paymentOptions?.serviceOptionRoutingCode ?? ''
      if (this.paymentMethod === 'cash') {  // Todo: Tim: not sure if we need this use case, but gonna cover it just in case
        this.navigateToTransactionFlow()
        this.errorService.open(this.cmsLabels.stellar.error.somethingHasGoneWrong, this.cmsLabels.stellar.dismiss)
      }
    })

    this.store.select(selectAuthState)
      .pipe(takeUntil(this.destroy$))
      .subscribe(authState => {
        this.userDigitalProfileId = authState.digitalProfileId
        this.risk = authState.risk
        if (authState.riskStatus?.isRedirectRequired) {
          this.navigateToTransactionFlow()
          this.errorService.open(this.cmsLabels.stellar.error.somethingHasGoneWrong, this.cmsLabels.stellar.dismiss)
        }
    })

    this.store.select(selectDestinationCountryData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(destinationCountryData => {
        this.destinationCountryCode = destinationCountryData.country!
        this.destinationCountrySubdivisionCode = destinationCountryData?.state ?? ''
    })

    this.store.select(selectSelectedBankAccount)
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedBankAccount => {
        this.selectedBankAccount = selectedBankAccount ?? null
    })
    
    this.countries$ = this.store.select(countriesInfo)
    this.isWalletFeeEnabled$ = this.store.select(isWalletFeeEnabled)
    this.selectedLocation$ = this.store.select(getSelectedLocationTrns)
    this.cmsLabels$ = this.store.select(getCurrentLabelsJson)
    this.subdivisionOptions$ = this.store.select(stateInfo)
    this.feeQuoteState$ = this.store.select(selectFeeQuoteState)
    this.enumerationsResponse$ = this.store.pipe(select(selectEnumFields))
    
    this.cmsLabels$
      .pipe(takeUntil(this.destroy$))
      .subscribe((labels) => {
        if (labels?.labels?.stellar ?? false) {
          this.cmsLabels = labels.labels
        }
        else {
          this.navigateToTransactionFlow()
        }
    })
    
    this.subdivisionOptions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((subdivisions) => {
        this.subdivisionOptions = subdivisions?.states ?? []
    })

    this.feeQuoteState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((feeQuoteData) => {
        this.exchangeRate = this.operation === 'withdraw' ? 
          feeQuoteData?.transactions[0]?.payoutAmountDetails?.fxRate ?? '???' :
          feeQuoteData?.transactions[0]?.sendAmountDetails?.fxRate ?? '???'
        this.sendAmount = feeQuoteData?.transactions[0]?.sendAmountDetails?.amount?.value!
        this.sendCurrency = this.operation === 'withdraw' ? 
          'USDC' :
          feeQuoteData?.transactions[0]?.sendAmountDetails?.amount?.currencyCode ?? '???'
        this.payoutCurrency = this.operation === 'withdraw' ?
          feeQuoteData?.transactions[0]?.payoutAmountDetails?.amount?.currencyCode ?? '???':
          'USDC'
    })

    this.actions$.pipe(
      ofType(
        DpSendValidateSuccess
      ),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      const response = action.sendValidateResponse
      this._sendValResponseError$.next(response.errors!)
      const gffpErrors = (response.errors && 
        response.errors.find((error: any) => 
          error.category && GFFP_FIELDS_CATEGORY.includes(error.category)
        )?.offendingFields?.map((fieldObj: { field: any }) => 
          fieldObj.field
        )) || undefined!;
      this._gffpFieldNames.next(gffpErrors);

      if (!response.readyForCommit && gffpErrors) {
        this.currentStep = this.steps['gffp']
      }
      else if (response.readyForCommit && (!response.errors || response.errors.length === 0)) {
        this.store.select(selectTransactionState)
          .pipe(takeUntil(this.destroy$))
          .subscribe((transactionData) => {
            this.transactionData = transactionData
        })
        this._summaryData$.next(this.transactionData)
        if (this.paymentMethod === 'debit') {
          this.currentStep = this.steps['paymentPlatform']
          this.launchPaymentPlatformSdk = true
        }
        else if (this.paymentMethod === 'bank') {
          this.currentStep = this.steps['summary']
        }
      }
      else {
        console.error('SOMETHING went wrong during sendValidate') // Todo: remove: #may6
        // Todo: Tim: #may6 - need error handling 
      }
    })

    this.store.select(selectsendCommitResponse)
      .pipe(takeUntil(this.destroy$))
      .subscribe((commitResponse) => {
        if (commitResponse?.transactionId) {
          this.commitResponse = commitResponse
          this.currentStep = this.steps['confirmation']
        }
    })

    if (this.paymentMethod === 'debit') {
      this.initiateSendValidate()
    }
    else if (this.paymentMethod === 'bank') {
      this.currentStep = this.steps['bankAccount']
    }
    else {
      this.navigateToTransactionFlow()
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleBackButton() {
    if (this.currentStep === this.steps['bankAccount'] && this.bankMode === 'add') {
      this.updateBankMode('select')
    }
    else if (this.currentStep === this.steps['summary']) {
      this.currentStep = this.steps['bankAccount']
    }
  }
  
  public displayBackButton() {
    if (this.currentStep === this.steps['bankAccount'] && this.bankMode === 'add') {
      return true
    }
    else if (this.currentStep === this.steps['summary']) {
      return true
    }
    return false
  }



  // Todo: Tim: Copy/paste from home.component.ts, needs refactoring
  public onGffpSubmit(data: any) { // Todo: Tim: fix type 
    const familyNames = pick(['additionalFamilyNames'], data.sender)
    const senderWithoutFamilyNames = omit(
      ['additionalFamilyNames'],
      data.sender
    )
    // this.error = ''
    // let payload = { ...this.sendValidatePayload }
    let senderPayload = {}
    senderPayload = {
      ...this.sendValidatePayload.sender,
      ...senderWithoutFamilyNames,
    }

    // if (
    //   !isEmpty(familyNames) &&
    //   pathOr('', ['additionalFamilyNames', 'mothersMaidenName'], familyNames)
    // ) {
    //   const familyArray = [
    //     {
    //       lastName: pathOr(
    //         '',
    //         ['additionalFamilyNames', 'mothersMaidenName'],
    //         familyNames
    //       ),
    //       type: 'MOTHER_S_MAIDEN_NAME',
    //     },
    //   ]
    //   senderPayload['additionalFamilyNames'] = familyArray
    // }

    // payload.sender = { ...senderPayload }
    this.sendValidatePayload = {
      ...this.sendValidatePayload,
      sender: {
        ...this.sendValidatePayload.sender,
        ...senderPayload,
      },
    }
    
    // if (
    //   senderWithoutFamilyNames.additionalTransactionDetails ||
    //   data.additionalTransactionDetails
    // ) {
    //   this.sendValidatePayload = {
    //     ...this.sendValidatePayload,
    //     additionalTransactionDetails: {
    //       getRelationshipToReceiver: true,
    //       ...data.additionalTransactionDetails,
    //     },
    //   }
    // }
    // this._analyticsBtnClickEvent('onGffpSubmitEvent', PageTitles.IDENTITY_PAGE)
    // this._summaryData$.next(undefined) // resetting summary data
    this.store.dispatch(
      DpSendValidate({
        transactionId: this.transactionId,
        sendValidatePayload: this.sendValidatePayload,
        endpoint: this.endpointVersion,
        operation: this.operation,
        fbAuthToken: this.firebaseAuthToken,
        isNewBankAccount: this.isNewBankAccount
      })
    )
  }

  public updateBankMode(mode: BankAccountMode): void {
    this.bankMode = mode
  }

  public initiateSendValidate(): void {
    const enumPayload = {
      userLanguage: 'en-US',
      targetAudience: 'CUSTOMER_FACING',
      countryCode: this.country,
      language: this.locale?.substring(0, 2)
    }

    this.sendValidatePayload = this.buildDPSendValidatePayload()

    this.store.dispatch(DpGetEnums({ 
        getEnumRequest: enumPayload, 
        endpoint: this.endpointVersion, 
        sendValidatePayload: this.sendValidatePayload, 
        transactionId: this.transactionId, 
        operation: this.operation,
        isNewBankAccount: this.isNewBankAccount,
        fbAuthToken: this.firebaseAuthToken,
        cmsLabels: this.cmsLabels
      }))
  }

  public paymentPlatformCompletion(event: 'success' | 'error'): void {
    if (event === 'success') {
      this.currentStep = this.steps['summary']
    }
    else {
      console.error('error during debit card payment')
      this.navigateToTransactionFlow()
      this.errorService.open(this.cmsLabels.stellar.error.somethingHasGoneWrong, this.cmsLabels.stellar.dismiss)
    }
  }

  public onCommit(): void {
      // this._analyticsBtnClickEvent(
      //   'onSummarySubmitEvent',
      //   PageTitles.SUMMARY_PAGE
      // );
      // this.error = '';
      this.store.dispatch(
        DpSendCommit({
          transactionId: this.transactionId,
          locale: this.locale,
          endpoint: this.endpointVersion
        })
      )
      // this.showBackIcon = false;
      // // console.log(" this.stepper 01 : ",  this.stepper);
      // this.stepper.selectedIndex = this.steps['confirmation'];
      // // console.log(" this.stepper 02 : ",  this.stepper);
      // this._analyticsPageNavigation(
      //   'loadedConfirmationPage',
      //   PageTitles.CONFIRMATION_PAGE
      // );
    }

  public navigateToTransactionFlow(): void {
    this.router.navigate(['/'], { queryParamsHandling: 'preserve' })
  }

  public buildDPSendValidatePayload(): DpSendValidatePayload {
    let sendValidatePayload: DpSendValidatePayload = {
      targetAudience: 'CONSUMER_FACING',
      userLanguage: 'en-US',
      destinationCountryCode: this.destinationCountryCode,
      destinationCountrySubdivisionCode: this.destinationCountrySubdivisionCode,
      payoutOptionCode: this.payoutOptionCode,
      payoutOptionRoutingCode: this.payoutOptionRoutingCode,
      sendAmount: {
        value: this.sendAmount,
        currencyCode: this.sendCurrency
      },
      payoutCurrencyCode: this.payoutCurrency,
      additionalTransactionDetails: {
        getRelationshipToReceiver: true
      },
      receiverSameAsSender: true,
      digitalProfileId: this.userDigitalProfileId,
      riskStatus: this.risk ?? '',
    }
    
    if (this.operation === 'deposit') {
      sendValidatePayload.fundingType = 'DEBIT'
    }

    if (this.operation === 'withdraw' && this.paymentMethod === 'bank') {
      if (this.selectedBankAccount?.targetAccountProfileId && !this.selectedBankAccount.targetAccountProfileId.includes('newaccount-')) {
        sendValidatePayload.targetAccountProfileId = this.selectedBankAccount?.targetAccountProfileId
        this.isNewBankAccount = false
      }
      else if (
        this.selectedBankAccount && 
        (this.selectedBankAccount?.newBankAccountInfo ?? false)
      ) {
        const targetAccountFields = this.formatNewBankAccountFields(this.selectedBankAccount.newBankAccountInfo)
        sendValidatePayload.targetAccount = targetAccountFields
        this.isNewBankAccount = true
      }
    }
    return sendValidatePayload
  }

  private formatNewBankAccountFields(newBankFields: {[key: string]: string}): {[key: string]: string} {
    const formattedFields: {[key: string]: string} = {}
    for (const [key, value] of Object.entries(newBankFields)) {
      if (key.startsWith('targetAccount')) {
        const newKey = key.replace('targetAccount', '')
        formattedFields[newKey.charAt(0).toLowerCase() + newKey.slice(1)] = value
      }
    }
    return formattedFields
  }
}
